import { createReducer, combineReducers } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { Action, Nullable } from 'modules/types/common';
import * as actions from 'store/testResults/actions';
import { switchAccountSuccess } from 'store/account/actions';
import { createFetchReducer, FetchingReducerState } from 'store/setLocalStore';
import { DAYJS_FORMAT } from 'lib/constants';
import {
  TestResult,
  TestResultDetails,
  TestResultsList,
  TestResultWithDetails,
  TestResultsFilter,
  DateRangeFilterParams
} from './types';
import {
  labHistoricalReducer,
  LabHistoricalState,
  shareLabResultsReducer,
  ShareLabResultsState,
  LabDetailsDownloadsState,
  labDetailsDownloadsReducer,
  patientLetterDownloadsReducer,
  PatientLetterDownloadsState
} from './lab/reducers';
import {
  microbiologyHistoricalReducer,
  MicrobiologyHistoricalState,
  shareMicrobiologyResultsReducer,
  ShareMicrobiologyResultsState,
  MicrobiologyDetailsDownloadsState,
  microbiologyDetailsDownloadsReducer
} from './microbiology/reducers';
import {
  shareImagingResultsReducer,
  ShareImagingResultsState,
  ImagingDetailsDownloadsState,
  imagingDetailsDownloadsReducer
} from './imaging/reducers';
import {
  sharePathologyResultsReducer,
  SharePathologyResultsState,
  PathologyDetailsDownloadsState,
  pathologyDetailsDownloadsReducer
} from './pathology/reducers';
import { GET_LAB_WITH_DETAILS, GET_TEST_RESULT_LIST_BY_ENCOUNTER } from './actions.common';

export type TestResultWithDetailsState = FetchingReducerState<TestResultWithDetails>;
export type TestResultListByEncounterState = FetchingReducerState<TestResult[]>;

// TEST RESULTS STATE
export interface TestResultsState {
  testResultsList: TestResultsListState;
  testResultDetails: TestResultDetailsState;
  testResultListByEncounter: TestResultListByEncounterState;
  testResultWithDetails: TestResultWithDetailsState;
  labHistorical: LabHistoricalState;
  shareLabResults: ShareLabResultsState;
  shareMicrobiologyResults: ShareMicrobiologyResultsState;
  shareImagingResults: ShareImagingResultsState;
  sharePathologyResults: SharePathologyResultsState;
  downloadLabDetails: LabDetailsDownloadsState;
  downloadPatientLetter: PatientLetterDownloadsState;
  downloadMicrobiologyDetails: MicrobiologyDetailsDownloadsState;
  downloadImagingDetails: ImagingDetailsDownloadsState;
  downloadPathologyDetails: PathologyDetailsDownloadsState;
  microbiologyHistorical: MicrobiologyHistoricalState;
}

// *************************
// ** TEST RESULTS LIST **
// *************************

export interface TestResultsListState {
  data: TestResultsList | TestResult[];
  error: Nullable<Error>;
  isFetching: boolean;
  lastUpdated: string | undefined;
  sortBy: string;
  filters: TestResultsFilter[];
  filterOptions: TestResultsFilter[];
  year: string;
  startDate: string;
  endDate: string;
}

export const testResultListFilterInitialState = {
  sortBy: 'desc',
  filters: [],
  filterOptions: [],
  year: '',
  startDate: '',
  endDate: ''
};

export const testResultsListFilterReducers = {
  [actions.UPDATE_SORT]: (state: TestResultsListState, action: { payload: { sortBy: string } }) => {
    state.sortBy = action.payload.sortBy;
  },
  [actions.UPDATE_FILTER_OPTIONS]: (
    state: TestResultsListState,
    action: { payload: { filterOptions: TestResultsFilter[] } }
  ) => {
    state.filterOptions = action.payload.filterOptions;
  },
  [actions.APPLY_TEST_RESULTS_FILTERS]: (state: TestResultsListState) => {
    state.filters = state.filterOptions?.filter(
      (filterOption: TestResultsFilter) => filterOption.checked
    );
  },
  [actions.RESET_TEST_RESULTS_FILTERS]: (state: TestResultsListState) => {
    state.filters = [];
    state.filterOptions = state.filterOptions?.map((filterOption: TestResultsFilter) => {
      return {
        ...filterOption,
        checked: false
      };
    });
    state.year = '';
    state.startDate = dayjs().format(DAYJS_FORMAT);
    state.endDate = dayjs().format(DAYJS_FORMAT);
  },
  [actions.UPDATE_DATE_RANGE]: (
    state: TestResultsListState,
    action: { payload: DateRangeFilterParams }
  ) => {
    state.year = action.payload.year;
    state.startDate = action.payload.startDate;
    state.endDate = action.payload.endDate;
  }
};

// *************************
// ** TEST RESULT DETAILS **
// *************************

export interface TestResultDetailsState {
  isFetching: boolean;
  data: TestResultDetails | null;
  error: Nullable<Error>;
}

export const testResultDetailsInitialState = (): TestResultDetailsState => ({
  isFetching: false,
  data: null,
  error: null
});

export const testResultDetailsReducer = createReducer(testResultDetailsInitialState(), {
  [actions.GET_TEST_RESULT_DETAILS]: state => {
    state.isFetching = true;
    state.error = null;
  },
  [actions.GET_TEST_RESULT_DETAILS_FAIL]: (state, action) => {
    state.isFetching = false;
    state.error = action.error;
  },
  [actions.GET_TEST_RESULT_DETAILS_SUCCESS]: (state, action) => {
    const { data, headers } = action.payload;
    const contentType = data.contentType || headers['content-type'];

    const result = {
      contentType,
      content: action.payload.data
    };

    state.isFetching = false;
    state.data = result;
  },
  [actions.RESET_TEST_RESULT_DETAILS]: () => testResultDetailsInitialState(),
  [switchAccountSuccess.type]: () => testResultDetailsInitialState()
});

const testResultsListReducer = createFetchReducer<TestResultsList>(
  actions.GET_TEST_RESULTS_LIST,
  true,
  testResultsListFilterReducers,
  testResultListFilterInitialState
);

const testResultsList = (state: TestResultsListState, action: Action) => {
  const newState = testResultsListReducer(state, action);
  if (action.type === actions.GET_MORE_TEST_RESULTS_LIST) {
    return { ...newState, isFetching: true };
  }
  return newState || {};
};

export default combineReducers({
  testResultsList,
  testResultDetails: testResultDetailsReducer,
  testResultListByEncounter: createFetchReducer<TestResult[]>(GET_TEST_RESULT_LIST_BY_ENCOUNTER),
  testResultWithDetails: createFetchReducer<TestResultWithDetails>(GET_LAB_WITH_DETAILS),
  labHistorical: labHistoricalReducer,
  shareLabResults: shareLabResultsReducer,
  shareMicrobiologyResults: shareMicrobiologyResultsReducer,
  shareImagingResults: shareImagingResultsReducer,
  sharePathologyResults: sharePathologyResultsReducer,
  microbiologyHistorical: microbiologyHistoricalReducer,
  downloadLabDetails: labDetailsDownloadsReducer,
  downloadPatientLetter: patientLetterDownloadsReducer,
  downloadMicrobiologyDetails: microbiologyDetailsDownloadsReducer,
  downloadImagingDetails: imagingDetailsDownloadsReducer,
  downloadPathologyDetails: pathologyDetailsDownloadsReducer
});
